import { HTMLAttributeAnchorTarget, ReactNode, useId, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AccountBalance,
  ArrowDownward,
  ArrowUpward,
  Business,
  ContactPage,
  Dashboard,
  DocumentScanner,
  ExpandLess,
  ExpandMore,
  Help,
  Home,
  Lock,
  Logout,
  Menu as MenuIcon,
  Person,
  Redeem,
  Rule,
  Settings,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import Logo from '@/components/Logo';
import { NavbarButton } from '@/features/navigation/components/NavbarButton';
import { NotificationsButton } from '@/features/notifications';
import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import DocumentService from '@/legacy/features/companies/documents/services/document';
import { Instance as CompanyService } from '@/legacy/services/company';
import { durandalRoutes } from '@/routes/durandal';

import { NavbarTopSpacer } from './NavbarTopSpacer';

const SETTINGS_MENU_ANCHOR_ORIGIN = { horizontal: 'right', vertical: 'bottom' } as const;
const SETTINGS_MENU_TRANSFORM_ORIGIN = { horizontal: 'right', vertical: 'top' } as const;
const NAVIGATION_MENU_ANCHOR_ORIGIN = { horizontal: 'left', vertical: 'bottom' } as const;
const NAVIGATION_MENU_TRANSFORM_ORIGIN = { horizontal: 'left', vertical: 'top' } as const;

type CurrentTab =
  | 'payments'
  | 'paymentRequests'
  | 'payees'
  | 'bank'
  | 'approvalRules'
  | 'documents'
  | 'dashboard';

type NavbarTopProps = {
  navigateHome: () => void;
  navigateSwitchCompany: () => void;
  logoutUser: () => void;
  settingsHref: string;
  currentTab: CurrentTab;
  shouldShowNavigation: boolean;
  shouldShowMyInvoices: boolean;
  shouldShowHelpCenter: boolean;
  shouldShowSwitchCompany: boolean;
  shouldShowReturnToDashboard: boolean;
  shouldShowReturnToHome: boolean;
  shouldShowUserSettings: boolean;
  shouldShowCompanySettings: boolean;
  shouldShowSsoSettings: boolean;
  shouldShowReferrals: boolean;
  shouldShowLogOut: boolean;
  myInvoicesUrl: string;
  myInvoicesCount: number;
  isSwitchCompanies: boolean;
  navigateToReferrals: () => void;
};

type AbstractItem = { key: string } & (
  | ({
      icon: ReactNode;
      label: ReactNode;
      active?: boolean;
      menuOnly?: boolean;
    } & (
      | { onClick?: () => void }
      | {
          href?: string;
          target?: HTMLAttributeAnchorTarget;
          rel?: string;
        }
    ))
  | { divider: true }
);

const toMenuItem = (item: AbstractItem, index: number, array: Array<AbstractItem>) => {
  if ('divider' in item) {
    if (index <= 0 || index >= array.length - 1 || 'divider' in array[index - 1]) {
      return null;
    }
    return <Divider key={item.key} />;
  }
  const { key, icon, label, active, menuOnly, ...menuItemProps } = item;
  return (
    <MenuItem
      key={key}
      selected={active}
      component={'href' in menuItemProps ? 'a' : undefined}
      {...menuItemProps}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );
};

const toNavButton = (item: AbstractItem) => {
  if ('divider' in item) {
    return <Box key={item.key} flexGrow={1} />;
  }
  const { key, icon, label, active, menuOnly, ...buttonProps } = item;
  if (menuOnly) {
    return null;
  }
  return (
    <NavbarButton key={key} active={active} {...buttonProps}>
      {label}
    </NavbarButton>
  );
};

const NavbarTop = ({
  navigateHome,
  navigateSwitchCompany,
  logoutUser,
  settingsHref,
  currentTab,
  shouldShowNavigation,
  shouldShowMyInvoices,
  shouldShowHelpCenter,
  shouldShowSwitchCompany,
  shouldShowReturnToDashboard,
  shouldShowReturnToHome,
  shouldShowUserSettings,
  shouldShowCompanySettings,
  shouldShowSsoSettings,
  shouldShowReferrals,
  shouldShowLogOut,
  myInvoicesUrl,
  myInvoicesCount,
  isSwitchCompanies,
  navigateToReferrals,
}: NavbarTopProps) => {
  const { formatMessage } = useIntl();
  const settingsMenuId = useId();
  const navMenuId = useId();
  const settingsMenuState = usePopupState({ variant: 'popover', popupId: settingsMenuId });
  const navMenuState = usePopupState({ variant: 'popover', popupId: navMenuId });
  const companyIsDemo = useSyncKoObservable(CompanyService.IsDemo);
  const companyName = useSyncKoObservable(CompanyService.DisplayName);
  const isFeatureOcrEnabled = useSyncKoObservable(DocumentService.isAccountingSoftwareSupported);

  const navItems = useMemo(() => {
    const items: Array<AbstractItem> = [];
    if (shouldShowNavigation) {
      items.push({
        key: 'dashboard',
        href: durandalRoutes.user.dashboard,
        active: currentTab === 'dashboard',
        icon: <Dashboard />,
        label: <FormattedMessage defaultMessage="Dashboard" />,
        menuOnly: true,
      });
      if (isFeatureOcrEnabled) {
        items.push({
          key: 'capture',
          href: durandalRoutes.user.capture,
          active: currentTab === 'documents',
          icon: <DocumentScanner />,
          label: <FormattedMessage defaultMessage="Capture" />,
        });
      }
      items.push(
        {
          key: 'payables',
          href: durandalRoutes.user.payables,
          active: currentTab === 'payments',
          icon: <ArrowUpward />,
          label: <FormattedMessage defaultMessage="Payables" />,
        },
        {
          key: 'receivables',
          href: durandalRoutes.user.receivables,
          active: currentTab === 'paymentRequests',
          icon: <ArrowDownward />,
          label: <FormattedMessage defaultMessage="Receivables" />,
        },
        {
          key: 'contacts',
          href: durandalRoutes.user.contacts,
          active: currentTab === 'payees',
          icon: <ContactPage />,
          label: <FormattedMessage defaultMessage="Contacts" />,
        },
        {
          key: 'accounts',
          href: durandalRoutes.user.accounts,
          active: currentTab === 'bank',
          icon: <AccountBalance />,
          label: <FormattedMessage defaultMessage="Accounts" />,
        },
        {
          key: 'approvals',
          href: durandalRoutes.user.approvals.default,
          active: currentTab === 'approvalRules',
          icon: <Rule />,
          label: <FormattedMessage defaultMessage="Approvals" />,
        }
      );
    }
    if (shouldShowMyInvoices) {
      items.push({
        key: 'my invoices',
        href: myInvoicesUrl,
        icon: <ArrowDownward />,
        label: (
          <>
            <FormattedMessage defaultMessage="My Invoices" />
            {myInvoicesCount > 0 && <Chip color="error" label={myInvoicesCount} sx={{ ml: 1 }} />}
          </>
        ),
      });
    }
    items.push({ key: 'divider', divider: true });
    if (shouldShowHelpCenter) {
      items.push({
        key: 'help center',
        href: 'https://www.plooto.com/support',
        target: '_blank',
        rel: 'noopener noreferrer',
        icon: <Help />,
        label: <FormattedMessage defaultMessage="Help Center" />,
      });
    }

    return items;
  }, [
    currentTab,
    isFeatureOcrEnabled,
    myInvoicesCount,
    myInvoicesUrl,
    shouldShowHelpCenter,
    shouldShowMyInvoices,
    shouldShowNavigation,
  ]);

  const settingsItems = useMemo(() => {
    const items: Array<AbstractItem> = [];
    if (shouldShowSwitchCompany) {
      items.push({
        key: 'switch company',
        onClick: navigateSwitchCompany,
        icon: <Business />,
        label: <FormattedMessage defaultMessage="Switch Company" />,
      });
    }
    if (shouldShowReturnToDashboard) {
      items.push({
        key: 'return to dashboard',
        href: durandalRoutes.user.dashboard,
        icon: <Dashboard />,
        label: <FormattedMessage defaultMessage="Return to main dashboard" />,
      });
    }
    if (shouldShowReturnToHome) {
      items.push({
        key: 'return to home',
        href: durandalRoutes.user.companySelect,
        icon: <Home />,
        label: <FormattedMessage defaultMessage="Return to Home" />,
      });
    }
    items.push({ key: 'divider1', divider: true });
    if (shouldShowUserSettings) {
      items.push({
        key: 'personal settings',
        href: durandalRoutes.user.settings.profile,
        icon: <Person />,
        label: <FormattedMessage defaultMessage="My Profile" />,
      });
    }
    if (shouldShowCompanySettings) {
      items.push({
        key: 'company settings',
        href: settingsHref,
        icon: <Settings />,
        label: <FormattedMessage defaultMessage="Settings" />,
      });
    }
    if (shouldShowSsoSettings) {
      items.push({
        key: 'sso settings',
        href: durandalRoutes.user.settings.sso,
        icon: <Lock />,
        label: <FormattedMessage defaultMessage="Single Sign-On" />,
      });
    }
    if (shouldShowReferrals) {
      items.push({
        key: 'referrals',
        href: durandalRoutes.user.referrals,
        onClick: navigateToReferrals,
        icon: <Redeem />,
        label: <FormattedMessage defaultMessage="Get 4 Months Free" />,
      });
    }
    items.push({ key: 'divider2', divider: true });
    if (shouldShowLogOut) {
      items.push({
        key: 'log out',
        onClick: logoutUser,
        icon: <Logout />,
        label: <FormattedMessage defaultMessage="Log out" />,
      });
    }
    return items;
  }, [
    logoutUser,
    navigateSwitchCompany,
    navigateToReferrals,
    settingsHref,
    shouldShowCompanySettings,
    shouldShowLogOut,
    shouldShowReferrals,
    shouldShowReturnToDashboard,
    shouldShowReturnToHome,
    shouldShowSsoSettings,
    shouldShowSwitchCompany,
    shouldShowUserSettings,
  ]);

  const navMenuItems = useMemo(() => navItems.map(toMenuItem).filter(Boolean), [navItems]);
  const navButtons = useMemo(() => navItems.map(toNavButton).filter(Boolean), [navItems]);
  const settingsMenuItems = useMemo(
    () => settingsItems.map(toMenuItem).filter(Boolean),
    [settingsItems]
  );

  return (
    <>
      <AppBar
        data-testid="navigation"
        position="fixed"
        color="midnight"
        sx={{
          // Bootstrap's layout system is pre-flexbox, so it uses block elements with auto-margins
          // for centering. This works okay up until the mobile breakpoint, where it starts
          // centering everything that isn't block-level. MUI's AppBar uses flex, so it doesn't
          // force a 100% min-width like Bootstrap's nav does. Override those margins so that they
          // play nice together while we're still under the tyranny of the Bootstrap grid.
          '& .container': {
            '@media (max-width: 899px)': {
              mx: 0,
            },
            px: 0,
          },
        }}
      >
        {/* We're using the Bootstrap grid for now to align with the rest of the page. */}
        <div className="container">
          <Toolbar disableGutters>
            {/* Mobile nav */}
            {/* Note: Wrap in a minWidth box to ensure that the logo is balanced when centered. */}
            <Box display={{ lg: 'none' }} minWidth={{ xs: 2 * 45, sm: 'initial' }}>
              {navMenuItems.length > 0 && (
                <>
                  <IconButton {...bindTrigger(navMenuState)} size="large" color="inherit">
                    <MenuIcon titleAccess={formatMessage({ defaultMessage: 'Navigation' })} />
                  </IconButton>
                  <Menu
                    {...bindMenu(navMenuState)}
                    // This closes the menu via event bubbling.
                    onClick={navMenuState.close}
                    anchorOrigin={NAVIGATION_MENU_ANCHOR_ORIGIN}
                    transformOrigin={NAVIGATION_MENU_TRANSFORM_ORIGIN}
                  >
                    {navMenuItems}
                  </Menu>
                </>
              )}
            </Box>

            {/* Plooto logo, centered on mobile, but left-aligned otherwise. */}
            <Box flexGrow={1} display={{ sm: 'none' }} />
            <NavbarButton
              active={currentTab === 'dashboard'}
              onClick={navigateHome}
              data-testid="navigation-logo-link"
            >
              <Logo variant="wordmark" color="inverse" width={68} height={17} />
              <Box component="span" sx={visuallyHidden}>
                <FormattedMessage defaultMessage="Home" />
              </Box>
            </NavbarButton>
            <Box flexGrow={1} display={{ lg: 'none' }} />

            {/* Desktop nav, takes up a lot of space! */}
            {navButtons.length > 0 && (
              <Box display={{ xs: 'none', lg: 'contents' }}>{navButtons}</Box>
            )}

            {/* Settings menu, which we show as long as we can since it has the company name. */}
            {/* Note: Wrap in a minWidth box to ensure that the logo is balanced when centered. */}
            <Box minWidth={{ xs: 2 * 45, sm: 'initial' }} display="flex">
              {!isSwitchCompanies && <NotificationsButton />}
              {settingsMenuItems.length > 0 && (
                <>
                  {isSwitchCompanies ? (
                    <IconButton
                      {...bindTrigger(settingsMenuState)}
                      size="large"
                      color="inherit"
                      sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                      data-testid="dropdown-toggle-return-to-company-dashboard"
                    >
                      <Settings titleAccess={formatMessage({ defaultMessage: 'Settings' })} />
                    </IconButton>
                  ) : (
                    <NavbarButton
                      {...bindTrigger(settingsMenuState)}
                      active={false}
                      sx={{ display: { xs: 'none', sm: 'flex' } }}
                      endIcon={settingsMenuState.isOpen ? <ExpandLess /> : <ExpandMore />}
                    >
                      <Typography
                        variant="inherit"
                        component="span"
                        // Ordinarily, we would not customize typography, but we're using Typography
                        // here only for its truncation behaviour, and the parent button unfortunately
                        // is relying on its wrapper-less padding to avoid cutting off the text
                        // (centering via inline-flex), and that doesn't work when we put a
                        // font-inheriting Typography component beneath it.
                        lineHeight="normal"
                        noWrap
                        maxWidth={160}
                        data-testid="dropdown-toggle-company-name"
                        title={companyName}
                      >
                        {/* We need the spacer to prevent the whitespace from being collapsed. */}
                        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                        {companyIsDemo && <FormattedMessage defaultMessage="(Demo)" />}{' '}
                        {companyName || <FormattedMessage defaultMessage="No Company Selected" />}
                      </Typography>
                    </NavbarButton>
                  )}
                  <IconButton
                    {...bindTrigger(settingsMenuState)}
                    size="large"
                    color="inherit"
                    sx={{ display: { sm: 'none' } }}
                  >
                    <Settings
                      titleAccess={companyName || formatMessage({ defaultMessage: 'Settings' })}
                    />
                  </IconButton>
                  <Menu
                    {...bindMenu(settingsMenuState)}
                    // This closes the menu via event bubbling.
                    onClick={settingsMenuState.close}
                    anchorOrigin={SETTINGS_MENU_ANCHOR_ORIGIN}
                    transformOrigin={SETTINGS_MENU_TRANSFORM_ORIGIN}
                  >
                    {settingsMenuItems}
                  </Menu>
                </>
              )}
            </Box>
          </Toolbar>
        </div>
      </AppBar>
      <NavbarTopSpacer />
    </>
  );
};

export { NavbarTop, NavbarTopProps };
